import type { WalletAsset } from "../asset";
import type { LedgerEntry } from "../ledger-entry";

export const TxType = {
  DEPOSIT: "DEPOSIT",
  WITHDRAWAL: "WITHDRAWAL",
  TRANSFER: "TRANSFER",
  SWAP: "SWAP",
  PAYMENT: "PAYMENT",
  MINT: "MINT",
  BURN: "BURN",
  TRADE: "TRADE",
  REWARD: "REWARD",
  COMPLETED: "COMPLETED",
} as const;

export type TxType = (typeof TxType)[keyof typeof TxType];

export const TxSource = {
  USER: "USER",
  SYSTEM: "SYSTEM",
  ADMIN: "ADMIN",
  API: "API",
} as const;

export type TxSource = (typeof TxSource)[keyof typeof TxSource];

export const TxAction = {
  DEPOSIT: "DEPOSIT",
  WITHDRAWAL: "WITHDRAWAL",
  TRANSFER: "TRANSFER",
  MINT: "MINT",
  SWAP: "SWAP",
  BURN: "BURN",
  BUY: "BUY",
  SELL: "SELL",
  FEE: "FEE",
  LIQUIDITY_ADD: "LIQUIDITY_ADD",
  LIQUIDITY_REMOVE: "LIQUIDITY_REMOVE",
} as const;

export type TxAction = (typeof TxAction)[keyof typeof TxAction];

export const TxStatus = {
  PENDING: "PENDING",
  PROCESSING: "PROCESSING",
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
  CANCELLED: "CANCELLED",
  REVERSED: "REVERSED",
} as const;

export type TxStatus = (typeof TxStatus)[keyof typeof TxStatus];

export const TransactionDirection = {
  IN: "IN",
  OUT: "OUT",
  SELF: "SELF",
} as const;

export type TransactionDirection =
  (typeof TransactionDirection)[keyof typeof TransactionDirection];

export const CounterpartyType = {
  BLOCKCHAIN: "BLOCKCHAIN",
  USER: "USER",
  SYSTEM: "SYSTEM",
} as const;

export type CounterpartyType =
  (typeof CounterpartyType)[keyof typeof CounterpartyType];

export interface TransactionMeta {
  hasNextPage: boolean;
  hasPrevPage: boolean;
  limit: number;
  page: number;
  total: number;
  totalPages: number;
}

export interface Transaction {
  id: string;
  userId?: string | null;
  user?: null;
  walletAssetId?: string | null;
  walletAsset?: WalletAsset | null;
  txHash?: string | null;
  clientRef?: string | null;
  type: TxType;
  source?: TxSource | null;
  action?: TxAction | null;
  status: TxStatus;
  amount: string;
  tokenSymbol?: string | null;
  fromAddress: string;
  toAddress: string;
  meta?: Record<string, any> | null;
  ledgerEntry: LedgerEntry[];

  reversalReason: string;
  reversalCompletedAt: string;
  reversedAmount: string;
  isReversalProcessed: boolean;
  failedAt: string;
  createdAt: string;
}

export interface TransactionInterface {
  data: Transaction[];
  meta: TransactionMeta;
}

export interface FormattedTransaction {
  id: string;
  type: TxType;
  action: TxAction;
  direction: TransactionDirection;
  status: TxStatus;
  message: string;
  amount: {
    value: string;
    formatted: string;
    currency: string;
  };
  converted?: {
    currency?: string;
    direction?: string;
    formatted?: string;
    value?: string;
  };
  asset: {
    name: string;
    symbol: string;
    currency: string;
    token: {
      code: string;
      decimals: number;
      name: string;
      symbol: string;
      isNative: boolean;
    };
    network: {
      code: string;
      name: string;
    };
  };
  addresses: {
    from: string;
    to: string;
  };
  counterparty: {
    id: string;
    name: string;
    type: CounterpartyType;
  };
  metadata: {
    fee?: string;
    enabled?: boolean;
    netAmount?: string;
    principal: string;
    totalDebit?: string;
    amountTo?: string;
    tokenTo?: string;
    isToNative?: boolean;
    feeCurrency: {
      code: string;
      decimals: number;
      isFiat: boolean;
      symbol: string;
    };
  };
  timestamps: {
    createdAt: string;
    updatedAt: string;
  };
}

export interface FormattedTransactionInterface {
  data: FormattedTransaction[];
  meta: TransactionMeta;
}

export interface TransactionQuote {
  domain: "withdrawal" | "swap" | "transfer" | "trade" | "deposit";
  usdEquivalent?: string;
  exchangeRate: ExchangeRate | null;
  conversion: Conversion | null;
  fee: Fee;
  settlement: Settlement;
  accounting: Accounting;
}

export interface CurrencyInfo {
  symbol: string;
  code: string;
  decimals: number;
  isFiat: boolean;
}

export interface ExchangeRate {
  rate: string;
  baseCurrency: CurrencyInfo;
  quoteCurrency: CurrencyInfo;
}

export interface Conversion {
  sourceAmount: string;
  destinationAmount: string;
  exchangeRate: string;
}

export interface Fee {
  enabled: boolean;
  feeAmount: string;
  feeCurrency: CurrencyInfo;
}

export interface Settlement {
  principal: string;
  totalDebit: string;
  netAmount: string;
  feeAmount: string;
  debitCurrency: CurrencyInfo;
  creditCurrency: CurrencyInfo | null;
  feeCurrency: CurrencyInfo;
}

export interface Accounting {
  currency: string;
  source: AccountingEntry;
  destination: AccountingEntry;
  fee: AccountingEntry;
  settlement: AccountingSettlement;
}

export interface AccountingEntry {
  quantity: string | null;
  amount: string;
}

export interface AccountingSettlement {
  debitAmount: string;
  creditAmount: string;
}
